/**
 * Funil de Feedback - Componente Visual
 * Exibe as estatísticas de resultado das ligações em formato de funil
 */
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, ProgressBar, Badge, Spinner, Alert, Button } from 'react-bootstrap';
import { fetchEstatisticasFunil } from '../../services/api';
import './FunilFeedback.css';

const FunilFeedback = ({ vendedora = '', onRefresh, compact = false }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dados, setDados] = useState(null);

    useEffect(() => {
        carregarDados();
    }, [vendedora]);

    const carregarDados = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchEstatisticasFunil(vendedora);
            if (response.success) {
                setDados(response.data);
            } else {
                setError(response.message || 'Erro ao carregar dados');
            }
        } catch (err) {
            setError('Erro de conexão');
            console.error('[FunilFeedback] Erro:', err);
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => {
        if (!num) return '0';
        return new Intl.NumberFormat('pt-BR').format(num);
    };

    const formatCurrency = (num) => {
        if (!num) return 'R$ 0,00';
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(num);
    };

    if (loading) {
        return (
            <div className="funil-loading">
                <Spinner animation="border" variant="primary" size="sm" />
                <span className="ms-2">Carregando funil...</span>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="warning" className="m-3">
                <small>{error}</small>
            </Alert>
        );
    }

    if (!dados) {
        return (
            <Alert variant="info" className="m-3">
                <small>Nenhum dado de feedback encontrado</small>
            </Alert>
        );
    }

    const { resumo, funil } = dados;

    // Ordenar funil por quantidade
    const funilOrdenado = Object.entries(funil || {})
        .map(([key, value]) => ({ key, ...value }))
        .sort((a, b) => b.quantidade - a.quantidade);

    // Calcular máximo para proporção visual
    const maxQuantidade = Math.max(...funilOrdenado.map(f => f.quantidade), 1);

    if (compact) {
        // Versão compacta para widgets
        return (
            <div className="funil-compact">
                <div className="funil-compact-header">
                    <span className="funil-compact-title">📊 Funil de Retorno</span>
                    <Button variant="link" size="sm" onClick={carregarDados} className="p-0">
                        🔄
                    </Button>
                </div>
                <div className="funil-compact-stats">
                    <div className="funil-stat-mini">
                        <span className="funil-stat-value">{formatNumber(resumo?.totalDistribuidos)}</span>
                        <span className="funil-stat-label">Distribuídos</span>
                    </div>
                    <div className="funil-stat-mini">
                        <span className="funil-stat-value">{formatNumber(resumo?.totalContatados)}</span>
                        <span className="funil-stat-label">Contatados</span>
                    </div>
                    <div className="funil-stat-mini success">
                        <span className="funil-stat-value">{formatNumber(funil?.comprou?.quantidade || 0)}</span>
                        <span className="funil-stat-label">Compraram</span>
                    </div>
                </div>
                {funilOrdenado.slice(0, 4).map(item => (
                    <div key={item.key} className="funil-bar-mini">
                        <div className="funil-bar-info">
                            <span>{item.emoji} {item.label}</span>
                            <Badge bg="secondary">{item.quantidade}</Badge>
                        </div>
                        <ProgressBar
                            now={(item.quantidade / maxQuantidade) * 100}
                            style={{ backgroundColor: '#e9ecef', height: '6px' }}
                            variant="info"
                        />
                    </div>
                ))}
            </div>
        );
    }

    // Versão completa
    return (
        <div className="funil-container">
            {/* Header com Resumo */}
            <div className="funil-header">
                <div className="funil-title">
                    <h5 className="mb-0">📊 Funil de Feedback</h5>
                    <small className="text-muted">Resultados das ligações de recuperação</small>
                </div>
                <Button variant="outline-primary" size="sm" onClick={carregarDados}>
                    🔄 Atualizar
                </Button>
            </div>

            {/* Cards de KPIs */}
            <Row className="g-3 mb-4">
                <Col xs={6} md={3}>
                    <div className="funil-kpi-card">
                        <div className="funil-kpi-icon">📤</div>
                        <div className="funil-kpi-value">{formatNumber(resumo?.totalDistribuidos)}</div>
                        <div className="funil-kpi-label">Distribuídos</div>
                    </div>
                </Col>
                <Col xs={6} md={3}>
                    <div className="funil-kpi-card">
                        <div className="funil-kpi-icon">📞</div>
                        <div className="funil-kpi-value">{formatNumber(resumo?.totalContatados)}</div>
                        <div className="funil-kpi-label">Contatados</div>
                        <small className="text-muted">
                            {resumo?.totalDistribuidos > 0
                                ? `${((resumo?.totalContatados / resumo?.totalDistribuidos) * 100).toFixed(1)}%`
                                : '0%'}
                        </small>
                    </div>
                </Col>
                <Col xs={6} md={3}>
                    <div className="funil-kpi-card pending">
                        <div className="funil-kpi-icon">⏳</div>
                        <div className="funil-kpi-value">{formatNumber(resumo?.pendentes)}</div>
                        <div className="funil-kpi-label">Pendentes</div>
                    </div>
                </Col>
                <Col xs={6} md={3}>
                    <div className="funil-kpi-card success">
                        <div className="funil-kpi-icon">💰</div>
                        <div className="funil-kpi-value">{formatCurrency(resumo?.valorRecuperado)}</div>
                        <div className="funil-kpi-label">Recuperado</div>
                        <Badge bg="success">{resumo?.taxaConversao}% conversão</Badge>
                    </div>
                </Col>
            </Row>

            {/* Funil Visual */}
            <Card className="funil-visual-card">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <strong>Resultados por Categoria</strong>
                    <Badge bg="primary">{funilOrdenado.reduce((acc, f) => acc + f.quantidade, 0)} total</Badge>
                </Card.Header>
                <Card.Body>
                    {funilOrdenado.length === 0 ? (
                        <div className="text-center py-4">
                            <div style={{ fontSize: '3rem' }}>📊</div>
                            <p className="text-muted mb-0">Nenhum feedback registrado ainda</p>
                        </div>
                    ) : (
                        <div className="funil-bars">
                            {funilOrdenado.map((item, idx) => (
                                <div
                                    key={item.key}
                                    className="funil-bar-item"
                                    style={{ animationDelay: `${idx * 0.1}s` }}
                                >
                                    <div className="funil-bar-header">
                                        <span className="funil-bar-emoji">{item.emoji}</span>
                                        <span className="funil-bar-label">{item.label}</span>
                                        <span className="funil-bar-count">
                                            <strong>{item.quantidade}</strong>
                                            {item.valorTotal > 0 && (
                                                <small className="text-success ms-2">
                                                    {formatCurrency(item.valorTotal)}
                                                </small>
                                            )}
                                        </span>
                                    </div>
                                    <div className="funil-bar-progress">
                                        <div
                                            className="funil-bar-fill"
                                            style={{
                                                width: `${(item.quantidade / maxQuantidade) * 100}%`,
                                                backgroundColor: item.cor
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Por Vendedora */}
            {dados.porVendedora && dados.porVendedora.length > 0 && (
                <Card className="funil-vendedoras-card mt-4">
                    <Card.Header>
                        <strong>📊 Desempenho por Vendedora</strong>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="vendedoras-grid">
                            {dados.porVendedora.map((v, idx) => (
                                <div key={idx} className="vendedora-card">
                                    <div className="vendedora-header">
                                        <span className="vendedora-nome">{v.vendedora}</span>
                                        <Badge bg={v.convertidos > 0 ? 'success' : 'secondary'}>
                                            {v.convertidos} 🛒
                                        </Badge>
                                    </div>
                                    <div className="vendedora-stats">
                                        <div className="vendedora-stat">
                                            <span className="stat-value">{v.total}</span>
                                            <span className="stat-label">Total</span>
                                        </div>
                                        <div className="vendedora-stat">
                                            <span className="stat-value">{v.contatados}</span>
                                            <span className="stat-label">Contatados</span>
                                        </div>
                                        <div className="vendedora-stat success">
                                            <span className="stat-value">{v.positivos}</span>
                                            <span className="stat-label">Positivos</span>
                                        </div>
                                    </div>
                                    {v.valor > 0 && (
                                        <div className="vendedora-valor">
                                            💰 {formatCurrency(v.valor)}
                                        </div>
                                    )}
                                    <ProgressBar className="mt-2">
                                        <ProgressBar
                                            variant="success"
                                            now={v.total > 0 ? (v.convertidos / v.total) * 100 : 0}
                                            key={1}
                                        />
                                        <ProgressBar
                                            variant="info"
                                            now={v.total > 0 ? ((v.contatados - v.convertidos) / v.total) * 100 : 0}
                                            key={2}
                                        />
                                    </ProgressBar>
                                </div>
                            ))}
                        </div>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

export default FunilFeedback;
